/**
 * Testimonial Grid module
 *
 * Two-up grid of color-theme testimonial panes
 */

// Deps
import { createComponent, RendererContext } from "react-fela";
import { colors, shadows, gutters, breakpoints } from "~/styleguide";
import { fluid } from "~/styleguide/utils";
import get from "lodash.get";
import { getClient } from "~/modules/wordpress";
const wp = getClient();
import WordpressLink from "~/components/global/links";

// Components
import Visual from "~/components/shared/visual";

// Context
import {useState, useContext, useEffect} from "react";

let animationName = "";

export default function TestimonialCarousel({ block }) {
    let { resources, speed_carousel, number_testimonials, preload } = block;

    const taxonomies = resources.map((res) => res.term_id);
    const [items, setItems] = useState([]);
    const [isMobile, setIsMobile] = useState(false);
    const renderer = useContext(RendererContext);
    animationName = renderer.renderKeyframe(keyframes, {});

    const onResize = () => {
        setIsMobile(window.innerWidth < breakpoints.maxWidthTablet);
    };



    const getTestimonials = () => {
        return () =>
            wp
                .testimonials()
                .param("_fields", [
                    "acf",
                    "resource-topics"
                ])
                .embed();
    };

    const getItems = async () => {
        let filterQuery = getTestimonials()();
        let result = await filterQuery.get();
        if (result && result.length) {
            let new_items = result.filter((res) => res && res["resource-topics"] && taxonomies.includes(res["resource-topics"][0]));
            new_items = new_items.sort((a, b) => a.acf.position - b.acf.position);
            new_items.splice(number_testimonials);
            setItems(new_items);
        }
    }

    const getResources = () => {
        return (
            <CardContainer length={items.length} speed={speed_carousel} className="card-container-row-animate">
                {items.map((res, i) => {
                    const {
                        company_logo,
                        headshot,
                        testimonial,
                        name,
                        job_title,
                        link_content,
                    } = res.acf;

                    let link = get(
                        link_content && link_content[0],
                        ["link"]
                    );

                    let logo = get(
                        company_logo,
                        ["sizes", "medium"],
                        get(company_logo, "url")
                    );

                    let photo = get(
                        headshot,
                        ["sizes", "thumbnail"],
                    );

                        if (link) {
                            return (
                                <WordpressLink
                                    url = {link}
                                >
                                    <Testimonial
                                        key={i}
                                    >
                                        <TextContent>
                                            {`${testimonial.substring(0,172)}${testimonial.length > 172 ? "..." : ""}`}
                                        </TextContent>
                                        <PersonContent>
                                            {photo && (
                                                <Visual
                                                    load={preload}
                                                    image={photo}
                                                    extend={photoStyle}
                                                />
                                            )}
                                            <PersonData>
                                                {`${name}`}<br/>{job_title}
                                            </PersonData>
                                            {logo && (
                                                <Visual
                                                    load={preload}
                                                    image={logo}
                                                    extend={logoStyle}
                                                />
                                            )}
                                        </PersonContent>
                                    </Testimonial>
                                </WordpressLink>
                            );
                      } else {
                        return (
                                <Testimonial
                                    key={i}
                                >
                                    <TextContent>
                                        {`${testimonial.substring(0,172)}${testimonial.length > 172 ? "..." : ""}`}
                                    </TextContent>
                                    <PersonContent>
                                        {photo && (
                                            <Visual
                                                load={preload}
                                                image={photo}
                                                extend={photoStyle}
                                            />
                                        )}
                                        <PersonData>
                                            {`${name}`}<br/>{job_title}
                                        </PersonData>
                                        {logo && (
                                            <Visual
                                                load={preload}
                                                image={logo}
                                                extend={logoStyle}
                                            />
                                        )}
                                    </PersonContent>

                                </Testimonial>
                        );
                      }
                })}
            </CardContainer>
        )
    }

    useEffect(() => {
            getItems();

            if (window) {
                window.addEventListener("resize", onResize);
            }

            return () => {
                window.removeEventListener("resize", onResize);
            }
        }, []
    );

    return (
        <CardRow className="module-testimonial-carousel card-container">
            {getResources()}
            {!isMobile && getResources()}
        </CardRow>
    );
}

const keyframes = () => ({
    "0%": { transform: "translateX(0%);" },
    "100%": { transform: "translateX(-100%);" }
});

const CardRow = createComponent(() => ({
    display: "flex",
    alignItems: "center",
    minHeight: "400px",
    width: "100%",
    flexWrap: "wrap",
    flexFlow: "row nowrap",
    overflow: "hidden",

    "&:hover": {
        overflowX: "scroll",
        "& .card-container-row-animate": {
            animationPlayState: "paused",
        }
    },
    maxWidthMobile: {
        minHeight: "300px",

    },
}));

const CardContainer = createComponent(({ length, speed = 15 })=>({
    display: "flex",
    flexFlow: "row nowrap",
    justifyContent: "center",
    alignItems: "stretch",
    alignContent: "flex-start",
    position: "relative",
    left: 0,
    placeContent: "flex-start center",

    minWidthTablet: {
        animationDuration: `${length * speed}s`,
        animationIterationCount: "infinite",
        animationTimingFunction: "linear",
        animationName: `${animationName}`,
    },

    maxWidthMobile:{
        overflowY: "scroll",
        placeContent: "flex-start",
        paddingRight: "24px"
    },

    extend: [
        fluid("paddingV", gutters.vertical)
    ]
}));

const Testimonial = createComponent(() => ({
    padding: "25px",
    fontSize: 20,
    lineHeight: "120%",
    textAlight: "center",
    letterSpacing: "0.06em",
    color: colors.black,
    boxShadow: shadows.default,
    overflow: "hidden",
    background: colors.white,
    borderRadius: 12,
    willChange: "box-shadow",
    transition: "0.25s box-shadow ease",
    whenActive: {
        boxShadow: shadows.active
    },
    marginRight: 24,
    display: "flex",
    flexWrap: "wrap",
    flexDirection: "column",
    justifyContent: "space-between",

    width: 514,
    height: 260,
    flex: "0 0 auto",

    maxWidthMobile: {
        width: 290,
        height: 180,
        lineHeight: "80%",
    },
}));

const TextContent = createComponent(() => ({
    display: "flex",
    flexWrap: "wrap",
    textAlign: "center",
    maxWidthMobile: {
        fontSize: 12,
        height: "70%",
    },
}));

const PersonContent = createComponent(() => ({
    display: "flex",
    flexWrap: "wrap",
    height: "35%",
    alignItems: "center",
    maxWidthMobile: {
        height: "30%",
    },
}));

const PersonData = createComponent(() => ({
    fontSize: 14,
    lineHeight: "120%",
    display: "flex",
    alignItems: "center",
    width: "40%",
    maxWidthMobile: {
        fontSize: 12,
        width: "75%",
    },
}));

const photoStyle = {
    display: "block",
    marginRight: 12,
    height: 72,
    width: 72,
    borderRadius: "50%",
    overflow: "hidden",
    maxWidthMobile: {
        height: 42,
        width: 42,
    },

    "& .vv-align-middle": {
        display: "none"
    },
    "& .vv-visual": {
        height: "100%",
        width: "100%"
    },
    "& .vv-visual *": {
        height: "100%"
    },
};

const logoStyle = {
    display: "block",
    overflow: "hidden",
    "& .vv-align-middle": {
        display: "none"
    },
    "& .vv-visual": {
        height: "100%",
        width: "100%"
    },
    "& .vv-visual *": {
        height: "auto"
    },
    width: 130,
    margin: "auto 0 auto auto",
    height: "max-content",
    maxWidthMobile: {
        display: "none",
    },
};
